"use client";
import cn from "classnames";
import { Loader } from "components/shared/Loader";
import { upperFirst } from "lodash";
import { Link } from "components/shared/Link";
import styles from "./Button.module.scss";
import { baseUrl } from "const";

export type ButtonProps = {
  children: React.ReactNode;
  tag: "span" | "button" | "internalLink" | "externalLink" | "dashboardLink" | "subdomainLink";
  type?: "secondary" | "accent" | "black" | "primary";
  size?: "small" | "medium";
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  href?: string;
  htmlType?: "submit" | "button";
  fullWidth?: boolean;
  ariaLabel?: string;
  noHover?: boolean;
  onClick?: React.MouseEventHandler;
  subdomain?: string;
};

export const Button = ({
  children,
  type = "accent",
  className = "",
  disabled = false,
  htmlType = "submit",
  size = "medium",
  href,
  tag,
  loading,
  onClick = () => {},
  ariaLabel,
  noHover,
  fullWidth = false,
  subdomain,
}: ButtonProps) => {
  const classNames = cn(
    styles.button,
    styles[`buttonType${upperFirst(type)}`],
    styles[`buttonSize${upperFirst(size)}`],
    {
      [styles.noHover]: noHover,
      [styles.loading]: loading,
      [styles.buttonFullWidth]: fullWidth,
    },
    className
  );

  switch (tag) {
    case "externalLink":
      return (
        <a href={href} target="_blank" rel="noreferrer nofollow" className={classNames} onClick={onClick}>
          {children}
        </a>
      );
    case "dashboardLink":
      return (
        <a href={`https://app.${String(baseUrl) + href}`} className={classNames} onClick={onClick}>
          {children}
        </a>
      );
    case "internalLink":
      return (
        <Link href={String(href)} includeSubdomain className={classNames} onClick={onClick}>
          {children}
        </Link>
      );
    case "subdomainLink":
      return (
        <a href={`https://${subdomain}.${String(baseUrl) + href}`} className={classNames} onClick={onClick}>
          {children}
        </a>
      );
    case "button":
      return (
        <button
          type={htmlType}
          aria-label={htmlType === "submit" ? "submit" : ariaLabel}
          className={classNames}
          disabled={disabled || loading}
          onClick={onClick}
        >
          {loading ? (
            <span className={styles.loaderWrap}>
              <Loader className={styles.loader} />
              {children}
            </span>
          ) : (
            children
          )}
        </button>
      );
    case "span":
      return (
        <span className={classNames} onClick={onClick}>
          {children}
        </span>
      );
    default:
      return null;
  }
};
